import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface IAssistantStatus {
    configured: boolean;
    autoQaAvailable: boolean;
    model: string;
}

export interface IAssistantMessage {
    role: 'user' | 'assistant';
    content: string;
    // Actions the assistant performed while producing this reply (assistant messages only).
    actions?: IAssistantAction[];
}

export interface IAssistantAction {
    tool: string;
    summary: string;
    success: boolean;
    // The timesheet the action touched (QA tools) - lets open screens live-refresh themselves.
    timeSheetId?: number | null;
}

export interface IAssistantChatResponse {
    reply: string;
    autoQaActive: boolean;
    actions: IAssistantAction[];
}

export interface IAssistantSkill {
    id: number;
    name: string;
    content: string;
    createdOn: string;
}

// A knowledge-base reference document (SOW / unit-code definitions) - the extracted text lives
// server-side; the assistant retrieves excerpts per question via its search_knowledge tool.
export interface IAssistantDocument {
    id: number;
    title: string;
    category: string;
    provider: string;
    sourceFileName: string;
    contentLength: number;
    createdOn: string;
    updatedOn?: string | null;
    createdByName?: string | null;
}

// One excerpt the knowledge search returned (the admin page's search preview shows exactly what
// the assistant's search_knowledge tool would retrieve).
export interface IAssistantKnowledgeHit {
    documentId: number;
    title: string;
    category: string;
    provider: string;
    chunkIndex: number;
    excerpt: string;
    score: number;
}

const AUTO_QA_STORAGE_KEY = 'assistant-auto-qa';

// Client for the AI billing assistant. The conversation lives HERE (service = singleton) so it
// survives route changes while the app is open; the backend is stateless per turn.
@Injectable({ providedIn: 'root' })
export class AssistantService {

    private base = `${environment.adminApiUrl}/assistant`;

    /** The in-memory conversation shown in the panel (user + assistant turns). */
    public messages: IAssistantMessage[] = [];

    // Fired after a chat turn whose reply carried actions (QA edits, sign-offs, approvals...), so
    // screens showing the touched data (e.g. an open QA grid) can reload themselves live.
    private actionsSubject = new Subject<IAssistantAction[]>();
    public actionsApplied$ = this.actionsSubject.asObservable();

    constructor(private httpClient: HttpClient) { }

    /** Broadcast the actions a reply performed (called by the chat panel after each turn). */
    public notifyActions(actions: IAssistantAction[] | undefined | null): void {
        if (actions && actions.length > 0) {
            this.actionsSubject.next(actions);
        }
    }

    public get autoQa(): boolean {
        try {
            return sessionStorage.getItem(AUTO_QA_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    }

    public set autoQa(value: boolean) {
        try {
            if (value) {
                sessionStorage.setItem(AUTO_QA_STORAGE_KEY, '1');
            } else {
                sessionStorage.removeItem(AUTO_QA_STORAGE_KEY);
            }
        } catch {
            // Storage unavailable - the toggle just won't persist across reloads.
        }
    }

    public status(): Observable<IAssistantStatus> {
        return this.httpClient.get<IAssistantStatus>(`${this.base}/status`);
    }

    /** One chat turn: the full conversation is sent; the reply comes back when the server-side tool
     *  loop finishes (can take a while - the panel shows a typing indicator). */
    public chat(messages: IAssistantMessage[], autoQa: boolean): Observable<IAssistantChatResponse> {
        const payload = {
            messages: messages.map(m => ({ role: m.role, content: m.content })),
            autoQa
        };
        return this.httpClient.post<IAssistantChatResponse>(`${this.base}/chat`, payload);
    }

    public getSkills(): Observable<IAssistantSkill[]> {
        return this.httpClient.get<IAssistantSkill[]>(`${this.base}/skills`);
    }

    public deleteSkill(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.base}/skills/${id}`);
    }

    // ---- knowledge base (reference documents; gated by ai-assistant-knowledge) ----

    public getDocuments(): Observable<IAssistantDocument[]> {
        return this.httpClient.get<IAssistantDocument[]>(`${this.base}/documents`);
    }

    /** Upload a reference document: a file (text extracted server-side: pdf/docx/xlsx/txt/md/csv)
     *  or pasted text. Upserts by title, so re-uploading a revised SOW replaces the old one. */
    public saveDocument(title: string, category: string, provider: string, file?: File | null, content?: string | null): Observable<IAssistantDocument> {
        const form = new FormData();
        form.append('title', title ?? '');
        form.append('category', category ?? '');
        form.append('provider', provider ?? '');
        if (content) form.append('content', content);
        if (file) form.append('file', file, file.name);
        return this.httpClient.post<IAssistantDocument>(`${this.base}/documents`, form);
    }

    public deleteDocument(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.base}/documents/${id}`);
    }

    /** Preview what the assistant's search_knowledge tool would retrieve for a query. */
    public searchDocuments(query: string, category?: string, provider?: string): Observable<IAssistantKnowledgeHit[]> {
        let params = new HttpParams().set('query', query);
        if (category) params = params.set('category', category);
        if (provider) params = params.set('provider', provider);
        return this.httpClient.get<IAssistantKnowledgeHit[]>(`${this.base}/documents/search`, { params });
    }

    public clearConversation(): void {
        this.messages = [];
    }
}
